/**
 * ProvenanceBadge — the §6.2 per-tile/section/row provenance chip. Pure render of deriveBadge over the
 * server's meta.provenance: REAL -> a green TEXT chip ("REAL" / "REAL*" / "None on record"; H5: status
 * by color + LABEL, never a bare dot), MOCK/PARTIAL -> an amber labelled chip, DEFERRED -> a grey
 * labelled chip. Built ONLY from SkyBadge + SkyTooltip. NON-DISMISSIBLE (no onDismiss) so a mock surface
 * cannot be clicked away. Never hand-tags a tier. The badge width is constrained by the consumer (see
 * DataHealth.tsx) so a tint chip renders compact, never a full-width bar inside a grid cell.
 */
import { SkyBadge, SkyText, SkyTooltip } from "@skypointcloud/skypoint-ui";
import type { Provenance } from "@shared/contracts";
import { deriveBadge } from "./deriveBadge";

/**
 * @param showNote P1-7: when true and the derived view carries a feed/gap `note` (DEFERRED/MOCK), render
 *   it as an inline caption BELOW the chip. The chip label itself stays short + constant ("DEFERRED" /
 *   "MOCK") so it can never truncate; the caption (and the tooltip) carry the feed name with room to wrap.
 *   Consumers with horizontal space (Dashboard tile, Data Health) opt in; compact grid cells leave it off.
 */
export function ProvenanceBadge({ meta, showNote = false }: { meta?: Provenance; showNote?: boolean }) {
  const v = deriveBadge(meta);
  const badge = (
    <SkyBadge
      color={v.color}
      appearance={v.dot ? "filled" : "tint"}
      dot={v.dot}
      size="small"
      aria-label={v.label}
    >
      {v.dot ? undefined : v.label}
    </SkyBadge>
  );
  const chip = v.tooltip ? (
    <SkyTooltip content={v.tooltip} side="top">
      {badge}
    </SkyTooltip>
  ) : (
    badge
  );
  // The caption uses an AA-contrast muted token (text-muted-foreground #616161 on the page) and wraps,
  // so a long feed name (e.g. "vw_data_health sync") is fully readable and never clips like a tiny chip.
  if (showNote && v.note) {
    return (
      <span className="inline-flex flex-col items-end gap-0.5">
        {chip}
        <SkyText size={100} color="muted">
          {v.note}
        </SkyText>
      </span>
    );
  }
  return chip;
}
